import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useNavigate } from 'react-router-dom';
import { API_ENDPOINTS } from '../config/api';
import StashitLogo from '../components/Logo/StashitLogo';
import { toast } from 'react-hot-toast';
import { useQuery } from '@tanstack/react-query';

// =====================================================
// COMPREHENSIVE ADMIN CONSOLE - UNIFIED MANAGEMENT PLATFORM
// =====================================================

// Enhanced Types for Unified Management
interface SystemStats {
  // User metrics
  totalUsers: number;
  activeUsers: number;
  newUsersToday: number;
  userGrowthRate: number;

  // Transaction metrics
  totalTransactions: number;
  pendingTransactions: number;
  completedTransactions: number;
  failedTransactions: number;
  transactionSuccessRate: number;

  // Financial metrics
  totalRevenue: number;
  revenueToday: number;
  revenueGrowth: number;
  averageTransactionValue: number;

  // Automation metrics
  autoApprovedRequests: number;
  manualReviewRequests: number;
  automationRate: number;
  averageProcessingTime: number;

  // Security metrics
  fraudAttemptsBlocked: number;
  suspiciousActivities: number;
  securityIncidents: number;

  // System health
  apiResponseTime: number;
  errorRate: number;
  uptime: number;
}

interface Alert {
  id: string;
  type: 'info' | 'warning' | 'error' | 'success';
  title: string;
  message: string;
  timestamp: string;
  priority: 'low' | 'medium' | 'high' | 'critical';
  acknowledged: boolean;
  actionRequired?: boolean;
  actionUrl?: string;
}

interface EscrowRequest {
  id: string;
  errand_id: string;
  user_id: string;
  courier_id?: string;
  amount: number;
  status: 'pending' | 'approved' | 'funded' | 'completed' | 'billed' | 'paid' | 'cancelled';
  risk_score?: number;
  auto_approved?: boolean;
  created_at: string;
  errand?: { title: string; description?: string };
  user?: { full_name: string; phone: string; email: string };
  courier?: { full_name: string; phone: string; email: string };
}

interface CourierCompany {
  id: string;
  name: string;
  verification_status: 'pending' | 'verified' | 'rejected';
  is_active: boolean;
  total_deliveries?: number;
  success_rate?: number;
  average_rating?: number;
  revenue_generated?: number;
}

interface DriverApplication {
  id: string;
  user_id: string;
  full_name: string;
  status: 'pending' | 'approved' | 'rejected' | 'kyc_required';
  application_date: string;
  kyc_documents_count?: number;
  performance_score?: number;
}

const AdminConsole: React.FC = () => {
  const { user } = useAuth();
  const navigate = useNavigate();

  // Main state management
  const [activeTab, setActiveTab] = useState<'dashboard' | 'errands' | 'couriers' | 'drivers' | 'automation' | 'analytics' | 'security'>('dashboard');
  const [selectedItems, setSelectedItems] = useState<Set<string>>(new Set());
  const [bulkAction, setBulkAction] = useState<string>('');
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState<string>('all');

  // Data fetching with React Query
  const { data: systemStats, isLoading: statsLoading, refetch: refetchStats } = useQuery({
    queryKey: ['admin-stats'],
    queryFn: async () => {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_ENDPOINTS.BASE}/api/admin/stats`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (!response.ok) throw new Error('Failed to fetch stats');
      return response.json();
    },
    refetchInterval: 30000, // Real-time updates every 30 seconds
  });

  const { data: alerts = [], refetch: refetchAlerts } = useQuery({
    queryKey: ['admin-alerts'],
    queryFn: async () => {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_ENDPOINTS.BASE}/api/admin/alerts`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (!response.ok) throw new Error('Failed to fetch alerts');
      return response.json().then(data => data.alerts || []);
    },
    refetchInterval: 60000,
  });

  // Fetch real dashboard data for couriers and drivers
  const { data: courierStats, refetch: refetchCourierStats } = useQuery({
    queryKey: ['courier-dashboard-stats'],
    queryFn: async () => {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_ENDPOINTS.BASE}/api/admin/couriers/dashboard-stats`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (!response.ok) throw new Error('Failed to fetch courier stats');
      return response.json();
    },
    enabled: activeTab === 'couriers',
  });

  const { data: driverStats, refetch: refetchDriverStats } = useQuery({
    queryKey: ['driver-dashboard-stats'],
    queryFn: async () => {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_ENDPOINTS.BASE}/api/admin/drivers/dashboard-stats`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (!response.ok) throw new Error('Failed to fetch driver stats');
      return response.json();
    },
    enabled: activeTab === 'drivers',
  });



  const { data: escrowRequests = [], refetch: refetchEscrow } = useQuery({
    queryKey: ['escrow-requests', statusFilter],
    queryFn: async () => {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_ENDPOINTS.BASE}/api/admin/escrow-requests`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (!response.ok) throw new Error('Failed to fetch escrow requests');
      return response.json().then(data => data.escrowRequests || []);
    },
    enabled: activeTab === 'errands',
  });

  const { data: couriers = [], refetch: refetchCouriers } = useQuery({
    queryKey: ['courier-companies'],
    queryFn: async () => {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_ENDPOINTS.BASE}/api/couriers/admin/all`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (!response.ok) return []; // Fallback to empty array
      return response.json();
    },
    enabled: activeTab === 'couriers',
  });

  const { data: drivers = [], refetch: refetchDrivers } = useQuery({
    queryKey: ['driver-applications'],
    queryFn: async () => {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_ENDPOINTS.BASE}/api/driver-applications/admin/applications`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (!response.ok) return []; // Fallback to empty array
      return response.json().then(data => data.applications || []);
    },
    enabled: activeTab === 'drivers',
  });

  // Authorization check
  useEffect(() => {
    if (!user || user.role !== 'admin') {
      navigate('/');
    }
  }, [user, navigate]);

  // Computed values
  const criticalAlerts = useMemo(() =>
    alerts.filter(alert => alert.priority === 'critical' && !alert.acknowledged),
    [alerts]
  );

  const filteredEscrowRequests = useMemo(() => {
    return escrowRequests.filter(request => {
      const matchesSearch = !searchTerm ||
        request.errand?.title?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        request.user?.full_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        request.id.toLowerCase().includes(searchTerm.toLowerCase());

      const matchesStatus = statusFilter === 'all' || request.status === statusFilter;

      return matchesSearch && matchesStatus;
    });
  }, [escrowRequests, searchTerm, statusFilter]);

  const filteredCouriers = useMemo(() => {
    return couriers.filter(courier => {
      const matchesSearch = !searchTerm ||
        courier.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        courier.contact_person?.toLowerCase().includes(searchTerm.toLowerCase());

      const matchesStatus = statusFilter === 'all' || courier.verification_status === statusFilter;

      return matchesSearch && matchesStatus;
    });
  }, [couriers, searchTerm, statusFilter]);

  const filteredDrivers = useMemo(() => {
    return drivers.filter(driver => {
      const matchesSearch = !searchTerm ||
        driver.full_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        driver.email?.toLowerCase().includes(searchTerm.toLowerCase());

      const matchesStatus = statusFilter === 'all' || driver.status === statusFilter;

      return matchesSearch && matchesStatus;
    });
  }, [drivers, searchTerm, statusFilter]);

  // Action handlers
  const acknowledgeAlert = async (alertId: string) => {
    try {
      const token = localStorage.getItem('token');
      await fetch(`${API_ENDPOINTS.BASE}/api/admin/alerts/${alertId}/acknowledge`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      refetchAlerts();
      toast.success('Alert acknowledged');
    } catch (error) {
      toast.error('Failed to acknowledge alert');
    }
  };

  const bulkEscrowAction = async (action: string) => {
    if (selectedItems.size === 0) {
      toast.error('No items selected');
      return;
    }

    try {
      const token = localStorage.getItem('token');
      const itemIds = Array.from(selectedItems);

      if (action === 'approve') {
        const promises = itemIds.map(id =>
          fetch(`${API_ENDPOINTS.BASE}/api/admin/escrow-requests/${id}/approve`, {
            method: 'POST',
            headers: {
              'Authorization': `Bearer ${token}`,
              'Content-Type': 'application/json'
            },
            body: JSON.stringify({ admin_notes: 'Bulk approved' })
          })
        );
        await Promise.all(promises);
      } else if (action === 'bulk-auto-approve') {
        await fetch(`${API_ENDPOINTS.BASE}/api/admin/automation/bulk-auto-approve`, {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({ escrowRequestIds: itemIds })
        });
      }

      setSelectedItems(new Set());
      refetchEscrow();
      toast.success(`Successfully processed ${itemIds.length} requests`);
    } catch (error) {
      toast.error('Bulk operation failed');
    }
  };

  const bulkCourierAction = async (action: string) => {
    if (selectedItems.size === 0) {
      toast.error('No items selected');
      return;
    }

    try {
      const token = localStorage.getItem('token');
      const itemIds = Array.from(selectedItems);

      if (action === 'activate') {
        const promises = itemIds.map(id =>
          fetch(`${API_ENDPOINTS.BASE}/api/couriers/admin/${id}/status`, {
            method: 'PUT',
            headers: {
              'Authorization': `Bearer ${token}`,
              'Content-Type': 'application/json'
            },
            body: JSON.stringify({ is_active: true })
          })
        );
        await Promise.all(promises);
      } else if (action === 'verify') {
        const promises = itemIds.map(id =>
          fetch(`${API_ENDPOINTS.BASE}/api/couriers/admin/${id}/verify`, {
            method: 'PUT',
            headers: {
              'Authorization': `Bearer ${token}`,
              'Content-Type': 'application/json'
            },
            body: JSON.stringify({
              verificationStatus: 'verified',
              verificationNotes: 'Bulk verified'
            })
          })
        );
        await Promise.all(promises);
      }

      setSelectedItems(new Set());
      refetchCouriers();
      toast.success(`Successfully processed ${itemIds.length} couriers`);
    } catch (error) {
      toast.error('Bulk operation failed');
    }
  };

  const bulkDriverAction = async (action: string) => {
    if (selectedItems.size === 0) {
      toast.error('No items selected');
      return;
    }

    try {
      const token = localStorage.getItem('token');
      const itemIds = Array.from(selectedItems);

      if (action === 'approve') {
        const promises = itemIds.map(id =>
          fetch(`${API_ENDPOINTS.BASE}/api/driver-applications/admin/applications/${id}/approve`, {
            method: 'POST',
            headers: {
              'Authorization': `Bearer ${token}`,
              'Content-Type': 'application/json'
            },
            body: JSON.stringify({ reviewNotes: 'Bulk approved' })
          })
        );
        await Promise.all(promises);
      }

      setSelectedItems(new Set());
      refetchDrivers();
      toast.success(`Successfully processed ${itemIds.length} drivers`);
    } catch (error) {
      toast.error('Bulk operation failed');
    }
  };

  const handleSelectItem = (id: string) => {
    const newSelected = new Set(selectedItems);
    if (newSelected.has(id)) {
      newSelected.delete(id);
    } else {
      newSelected.add(id);
    }
    setSelectedItems(newSelected);
  };

  const handleSelectAll = () => {
    let items: any[] = [];
    switch (activeTab) {
      case 'errands': items = filteredEscrowRequests; break;
      case 'couriers': items = filteredCouriers; break;
      case 'drivers': items = filteredDrivers; break;
    }

    if (selectedItems.size === items.length) {
      setSelectedItems(new Set());
    } else {
      setSelectedItems(new Set(items.map(item => item.id)));
    }
  };

  // KYC completion for drivers
  const completeDriverKYC = async (driverId: string) => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_ENDPOINTS.BASE}/api/admin/drivers/${driverId}/complete-kyc`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ documents: [] }) // Documents will be validated on the server
      });

      if (response.ok) {
        const result = await response.json();
        toast.success(result.message || 'KYC completed successfully');
        refetchDrivers(); // Refresh the drivers list
      } else {
        const error = await response.json();
        toast.error(error.message || 'Failed to complete KYC');
      }
    } catch (error) {
      console.error('Error completing driver KYC:', error);
      toast.error('Failed to complete KYC');
    }
  };

  // Render functions
  const renderDashboard = () => (
    <div className="space-y-6">
      {/* Critical Alerts Banner */}
      {criticalAlerts.length > 0 && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4">
          <div className="flex items-center justify-between">
            <div className="text-center flex-1">
              <div className="flex items-center justify-center space-x-2">
                <div className="w-3 h-3 bg-red-500 rounded-full animate-pulse"></div>
                <span className="font-semibold text-red-800">
                  {criticalAlerts.length} Critical Alert{alerts.length !== 1 ? 's' : ''}
                </span>
              </div>
              <div className="mt-2 space-y-1">
                {criticalAlerts.slice(0, 2).map(alert => (
                  <div key={alert.id} className="text-sm text-red-700">
                    • {alert.title}
                  </div>
                ))}
              </div>
            </div>
            <button
              onClick={() => setActiveTab('security')}
              className="ml-4 text-red-600 hover:text-red-800 text-sm font-medium"
            >
              View All →
            </button>
          </div>
        </div>
      )}

      {/* Key Metrics Grid */}
      {systemStats && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <div className="bg-white rounded-lg shadow-sm border border-neutral-200 p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-neutral-600">Transaction Success</p>
                <p className="text-2xl font-bold text-green-600">{systemStats.transactionSuccessRate?.toFixed(1) || '0.0'}%</p>
              </div>
              <div className="w-12 h-12 bg-green-100 rounded-lg flex items-center justify-center">
                <span className="text-green-600 text-xl">📈</span>
              </div>
            </div>
            <div className="mt-4 text-xs text-neutral-500">
              {systemStats.completedTransactions || 0} of {systemStats.totalTransactions || 0} successful
            </div>
          </div>

          <div className="bg-white rounded-lg shadow-sm border border-neutral-200 p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-neutral-600">Automation Rate</p>
                <p className="text-2xl font-bold text-blue-600">{systemStats.automationRate?.toFixed(1) || '0.0'}%</p>
              </div>
              <div className="w-12 h-12 bg-blue-100 rounded-lg flex items-center justify-center">
                <span className="text-blue-600 text-xl">🤖</span>
              </div>
            </div>
            <div className="mt-4 text-xs text-neutral-500">
              {systemStats.autoApprovedRequests || 0} auto-approved this week
            </div>
          </div>

          <div className="bg-white rounded-lg shadow-sm border border-neutral-200 p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-neutral-600">Revenue Today</p>
                <p className="text-2xl font-bold text-green-600">P{systemStats.revenueToday?.toLocaleString() || '0'}</p>
              </div>
              <div className="w-12 h-12 bg-green-100 rounded-lg flex items-center justify-center">
                <span className="text-green-600 text-xl">💰</span>
              </div>
            </div>
            <div className="mt-4 text-xs text-neutral-500">
              {systemStats.revenueGrowth >= 0 ? '+' : ''}{systemStats.revenueGrowth?.toFixed(1) || '0'}% vs yesterday
            </div>
          </div>

          <div className="bg-white rounded-lg shadow-sm border border-neutral-200 p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-neutral-600">Security Status</p>
                <p className="text-2xl font-bold text-purple-600">{systemStats.fraudAttemptsBlocked || 0}</p>
              </div>
              <div className="w-12 h-12 bg-purple-100 rounded-lg flex items-center justify-center">
                <span className="text-purple-600 text-xl">🛡️</span>
              </div>
            </div>
            <div className="mt-4 text-xs text-neutral-500">
              Threats blocked this week
            </div>
          </div>
        </div>
      )}

      {/* Real-time Fleet and Driver Stats */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        {/* Courier Stats */}
        <div className="bg-white rounded-lg shadow-sm border border-neutral-200 p-6">
          <h3 className="text-lg font-semibold text-neutral-800 mb-4">Fleet Overview</h3>
          {courierStats ? (
            <div className="space-y-3">
              <div className="flex justify-between">
                <span className="text-neutral-600">Total Couriers</span>
                <span className="font-semibold">{courierStats.totalCouriers || 0}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-neutral-600">Active Couriers</span>
                <span className="font-semibold text-green-600">{courierStats.activeCouriers || 0}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-neutral-600">Total Deliveries</span>
                <span className="font-semibold">{courierStats.totalDeliveries || 0}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-neutral-600">Revenue Generated</span>
                <span className="font-semibold text-green-600">P{courierStats.revenueGenerated?.toLocaleString() || '0'}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-neutral-600">Success Rate</span>
                <span className="font-semibold">{courierStats.deliverySuccessRate?.toFixed(1) || '0.0'}%</span>
              </div>
            </div>
          ) : (
            <div className="text-center py-4">
              <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-blue-600 mx-auto"></div>
              <p className="text-sm text-neutral-500 mt-2">Loading fleet data...</p>
            </div>
          )}
        </div>

        {/* Driver Stats */}
        <div className="bg-white rounded-lg shadow-sm border border-neutral-200 p-6">
          <h3 className="text-lg font-semibold text-neutral-800 mb-4">Driver Workforce</h3>
          {driverStats ? (
            <div className="space-y-3">
              <div className="flex justify-between">
                <span className="text-neutral-600">Total Drivers</span>
                <span className="font-semibold">{driverStats.totalDrivers || 0}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-neutral-600">Approved Drivers</span>
                <span className="font-semibold text-green-600">{driverStats.approvedDrivers || 0}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-neutral-600">Pending Applications</span>
                <span className="font-semibold text-yellow-600">{driverStats.pendingApplications || 0}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-neutral-600">Total Errands</span>
                <span className="font-semibold">{driverStats.totalErrands || 0}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-neutral-600">Completion Rate</span>
                <span className="font-semibold">{driverStats.completionRate?.toFixed(1) || '0.0'}%</span>
              </div>
            </div>
          ) : (
            <div className="text-center py-4">
              <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-blue-600 mx-auto"></div>
              <p className="text-sm text-neutral-500 mt-2">Loading driver data...</p>
            </div>
          )}
        </div>
      </div>

      {/* Quick Actions Grid */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        <div className="bg-white rounded-lg shadow-sm border border-neutral-200 p-6">
          <h3 className="text-lg font-semibold text-neutral-800 mb-4">Errand Management</h3>
          <div className="space-y-3">
            <button
              onClick={() => setActiveTab('errands')}
              className="w-full text-left p-3 border border-neutral-200 rounded-lg hover:bg-neutral-50 transition-colors"
            >
              <div className="font-medium text-neutral-800">Escrow Processing</div>
              <div className="text-sm text-neutral-600">Manage financial transactions</div>
            </button>
            <div className="text-sm text-neutral-500">
              {escrowRequests.filter(r => r.status === 'pending').length} pending reviews
            </div>
          </div>
        </div>

        <div className="bg-white rounded-lg shadow-sm border border-neutral-200 p-6">
          <h3 className="text-lg font-semibold text-neutral-800 mb-4">Fleet Management</h3>
          <div className="space-y-3">
            <button
              onClick={() => setActiveTab('couriers')}
              className="w-full text-left p-3 border border-neutral-200 rounded-lg hover:bg-neutral-50 transition-colors"
            >
              <div className="font-medium text-neutral-800">Courier Companies</div>
              <div className="text-sm text-neutral-600">Manage delivery partners</div>
            </button>
            <button
              onClick={() => setActiveTab('drivers')}
              className="w-full text-left p-3 border border-neutral-200 rounded-lg hover:bg-neutral-50 transition-colors"
            >
              <div className="font-medium text-neutral-800">Driver Workforce</div>
              <div className="text-sm text-neutral-600">Manage driver applications</div>
            </button>
          </div>
        </div>

        <div className="bg-white rounded-lg shadow-sm border border-neutral-200 p-6">
          <h3 className="text-lg font-semibold text-neutral-800 mb-4">System Intelligence</h3>
          <div className="space-y-3">
            <button
              onClick={() => setActiveTab('automation')}
              className="w-full text-left p-3 border border-neutral-200 rounded-lg hover:bg-neutral-50 transition-colors"
            >
              <div className="font-medium text-neutral-800">Automation Rules</div>
              <div className="text-sm text-neutral-600">Configure smart processing</div>
            </button>
            <button
              onClick={() => setActiveTab('analytics')}
              className="w-full text-left p-3 border border-neutral-200 rounded-lg hover:bg-neutral-50 transition-colors"
            >
              <div className="font-medium text-neutral-800">Business Analytics</div>
              <div className="text-sm text-neutral-600">Performance insights</div>
            </button>
          </div>
        </div>
      </div>
    </div>
  );

  const renderErrandsTab = () => (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold text-neutral-800">Errand Management</h2>
          <p className="text-neutral-600">Manage escrow transactions and financial processing</p>
        </div>
        <div className="flex items-center space-x-4">
          {selectedItems.size > 0 && (
            <div className="flex items-center space-x-2">
              <span className="text-sm text-neutral-600">{selectedItems.size} selected</span>
              <select
                value={bulkAction}
                onChange={(e) => setBulkAction(e.target.value)}
                className="text-sm border border-neutral-300 rounded px-3 py-1"
              >
                <option value="">Bulk Actions</option>
                <option value="approve">Approve Selected</option>
                <option value="bulk-auto-approve">Auto-Approve Selected</option>
              </select>
              <button
                onClick={() => bulkEscrowAction(bulkAction)}
                disabled={!bulkAction}
                className="bg-blue-600 text-white px-4 py-1 rounded text-sm hover:bg-blue-700 disabled:opacity-50"
              >
                Apply
              </button>
            </div>
          )}
        </div>
      </div>

      {/* Filters */}
      <div className="bg-white rounded-lg shadow-sm border border-neutral-200 p-4">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <div>
            <input
              type="text"
              placeholder="Search errands..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full px-3 py-2 border border-neutral-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
          </div>
          <div>
            <select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              className="w-full px-3 py-2 border border-neutral-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              <option value="all">All Statuses</option>
              <option value="pending">Pending</option>
              <option value="approved">Approved</option>
              <option value="funded">Funded</option>
              <option value="completed">Completed</option>
              <option value="billed">Billed</option>
              <option value="paid">Paid</option>
            </select>
          </div>
          <div className="flex items-center">
            <button
              onClick={() => refetchEscrow()}
              className="bg-blue-600 text-white px-4 py-2 rounded hover:bg-blue-700"
            >
              Refresh
            </button>
          </div>
        </div>
      </div>

      {/* Escrow Requests Table */}
      <div className="bg-white rounded-lg shadow-sm border border-neutral-200 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-neutral-200">
            <thead className="bg-neutral-50">
              <tr>
                <th className="px-6 py-3 text-left">
                  <input
                    type="checkbox"
                    checked={selectedItems.size === filteredEscrowRequests.length && filteredEscrowRequests.length > 0}
                    onChange={handleSelectAll}
                    className="rounded border-neutral-300"
                  />
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-neutral-500 uppercase">Errand</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-neutral-500 uppercase">Customer</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-neutral-500 uppercase">Courier</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-neutral-500 uppercase">Amount</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-neutral-500 uppercase">Risk Score</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-neutral-500 uppercase">Status</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-neutral-500 uppercase">Actions</th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-neutral-200">
              {filteredEscrowRequests.map((request: EscrowRequest) => (
                <tr key={request.id} className="hover:bg-neutral-50">
                  <td className="px-6 py-4">
                    <input
                      type="checkbox"
                      checked={selectedItems.has(request.id)}
                      onChange={() => handleSelectItem(request.id)}
                      className="rounded border-neutral-300"
                    />
                  </td>
                  <td className="px-6 py-4">
                    <div>
                      <div className="font-medium text-neutral-900">{request.errand?.title || 'Untitled'}</div>
                      <div className="text-sm text-neutral-500">{request.id.slice(0, 8)}...</div>
                    </div>
                  </td>
                  <td className="px-6 py-4">
                    <div className="text-sm text-neutral-900">{request.user?.full_name || 'Unknown'}</div>
                    <div className="text-sm text-neutral-500">{request.user?.phone || ''}</div>
                  </td>
                  <td className="px-6 py-4">
                    <div className="text-sm text-neutral-900">{request.courier?.full_name || 'Unassigned'}</div>
                    <div className="text-sm text-neutral-500">{request.courier?.phone || ''}</div>
                  </td>
                  <td className="px-6 py-4 text-sm font-medium">P{request.amount?.toFixed(2)}</td>
                  <td className="px-6 py-4">
                    <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                      (request.risk_score || 0) < 30 ? 'bg-green-100 text-green-800' :
                      (request.risk_score || 0) < 70 ? 'bg-yellow-100 text-yellow-800' :
                      'bg-red-100 text-red-800'
                    }`}>
                      {request.risk_score || 0}
                    </span>
                  </td>
                  <td className="px-6 py-4">
                    <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                      request.status === 'pending' ? 'bg-yellow-100 text-yellow-800' :
                      request.status === 'approved' ? 'bg-blue-100 text-blue-800' :
                      request.status === 'funded' ? 'bg-green-100 text-green-800' :
                      request.status === 'completed' ? 'bg-purple-100 text-purple-800' :
                      request.status === 'billed' ? 'bg-orange-100 text-orange-800' :
                      request.status === 'paid' ? 'bg-green-100 text-green-800' :
                      'bg-gray-100 text-gray-800'
                    }`}>
                      {request.status}
                    </span>
                    {request.auto_approved && (
                      <div className="text-xs text-blue-600 mt-1">🤖 Auto-approved</div>
                    )}
                  </td>
                  <td className="px-6 py-4 text-sm">
                    {request.status === 'pending' && (
                      <button className="text-blue-600 hover:text-blue-900 mr-2">Review</button>
                    )}
                    {request.status === 'approved' && (
                      <button className="text-green-600 hover:text-green-900 mr-2">Fund</button>
                    )}
                    {request.status === 'completed' && (
                      <button className="text-orange-600 hover:text-orange-900 mr-2">Bill</button>
                    )}
                    {request.status === 'billed' && (
                      <button className="text-purple-600 hover:text-purple-900 mr-2">Pay</button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {filteredEscrowRequests.length === 0 && (
          <div className="text-center py-12">
            <p className="text-neutral-500">No escrow requests found matching your criteria.</p>
          </div>
        )}
      </div>
    </div>
  );

  const renderCouriersTab = () => (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold text-neutral-800">Courier Management</h2>
          <p className="text-neutral-600">Manage delivery partners and fleet operations</p>
        </div>
        <div className="flex items-center space-x-4">
          {selectedItems.size > 0 && (
            <div className="flex items-center space-x-2">
              <span className="text-sm text-neutral-600">{selectedItems.size} selected</span>
              <select
                value={bulkAction}
                onChange={(e) => setBulkAction(e.target.value)}
                className="text-sm border border-neutral-300 rounded px-3 py-1"
              >
                <option value="">Bulk Actions</option>
                <option value="activate">Activate Selected</option>
                <option value="verify">Verify Selected</option>
              </select>
              <button
                onClick={() => bulkCourierAction(bulkAction)}
                disabled={!bulkAction}
                className="bg-blue-600 text-white px-4 py-1 rounded text-sm hover:bg-blue-700 disabled:opacity-50"
              >
                Apply
              </button>
            </div>
          )}
        </div>
      </div>

      {/* Filters */}
      <div className="bg-white rounded-lg shadow-sm border border-neutral-200 p-4">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <div>
            <input
              type="text"
              placeholder="Search couriers..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full px-3 py-2 border border-neutral-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
          </div>
          <div>
            <select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              className="w-full px-3 py-2 border border-neutral-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              <option value="all">All Statuses</option>
              <option value="pending">Pending</option>
              <option value="verified">Verified</option>
              <option value="rejected">Rejected</option>
            </select>
          </div>
          <div className="flex items-center">
            <button
              onClick={() => refetchCouriers()}
              className="bg-blue-600 text-white px-4 py-2 rounded hover:bg-blue-700"
            >
              Refresh
            </button>
          </div>
        </div>
      </div>

      {/* Couriers Table */}
      <div className="bg-white rounded-lg shadow-sm border border-neutral-200 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-neutral-200">
            <thead className="bg-neutral-50">
              <tr>
                <th className="px-6 py-3 text-left">
                  <input
                    type="checkbox"
                    checked={selectedItems.size === filteredCouriers.length && filteredCouriers.length > 0}
                    onChange={handleSelectAll}
                    className="rounded border-neutral-300"
                  />
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-neutral-500 uppercase">Company</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-neutral-500 uppercase">Contact</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-neutral-500 uppercase">Performance</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-neutral-500 uppercase">Status</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-neutral-500 uppercase">Actions</th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-neutral-200">
              {filteredCouriers.map((courier: any) => (
                <tr key={courier.id} className="hover:bg-neutral-50">
                  <td className="px-6 py-4">
                    <input
                      type="checkbox"
                      checked={selectedItems.has(courier.id)}
                      onChange={() => handleSelectItem(courier.id)}
                      className="rounded border-neutral-300"
                    />
                  </td>
                  <td className="px-6 py-4">
                    <div>
                      <div className="font-medium text-neutral-900">{courier.name}</div>
                      <div className="text-sm text-neutral-500">#{courier.business_registration}</div>
                    </div>
                  </td>
                  <td className="px-6 py-4">
                    <div className="text-sm text-neutral-900">{courier.contact_person}</div>
                    <div className="text-sm text-neutral-500">{courier.email}</div>
                  </td>
                  <td className="px-6 py-4">
                    <div className="text-sm">
                      <div>Deliveries: {courier.total_deliveries || 0}</div>
                      <div>Success Rate: {courier.success_rate ? `${courier.success_rate.toFixed(1)}%` : 'N/A'}</div>
                      <div>Rating: {courier.average_rating ? `${courier.average_rating.toFixed(1)}⭐` : 'N/A'}</div>
                    </div>
                  </td>
                  <td className="px-6 py-4">
                    <div className="flex flex-col space-y-1">
                      <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                        courier.verification_status === 'verified' ? 'bg-green-100 text-green-800' :
                        courier.verification_status === 'pending' ? 'bg-yellow-100 text-yellow-800' :
                        'bg-red-100 text-red-800'
                      }`}>
                        {courier.verification_status}
                      </span>
                      <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                        courier.is_active ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'
                      }`}>
                        {courier.is_active ? 'Active' : 'Inactive'}
                      </span>
                    </div>
                  </td>
                  <td className="px-6 py-4 text-sm">
                    <button className="text-blue-600 hover:text-blue-900 mr-2">View Details</button>
                    <button className="text-green-600 hover:text-green-900 mr-2">Manage Drivers</button>
                    <button className={`text-xs px-2 py-1 rounded ${
                      courier.is_active ? 'bg-red-100 text-red-800' : 'bg-green-100 text-green-800'
                    }`}>
                      {courier.is_active ? 'Deactivate' : 'Activate'}
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {filteredCouriers.length === 0 && (
          <div className="text-center py-12">
            <p className="text-neutral-500">No courier companies found matching your criteria.</p>
          </div>
        )}
      </div>
    </div>
  );

  const renderDriversTab = () => (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold text-neutral-800">Driver Management</h2>
          <p className="text-neutral-600">Manage driver applications and workforce</p>
        </div>
        <div className="flex items-center space-x-4">
          {selectedItems.size > 0 && (
            <div className="flex items-center space-x-2">
              <span className="text-sm text-neutral-600">{selectedItems.size} selected</span>
              <select
                value={bulkAction}
                onChange={(e) => setBulkAction(e.target.value)}
                className="text-sm border border-neutral-300 rounded px-3 py-1"
              >
                <option value="">Bulk Actions</option>
                <option value="approve">Approve Selected</option>
              </select>
              <button
                onClick={() => bulkDriverAction(bulkAction)}
                disabled={!bulkAction}
                className="bg-blue-600 text-white px-4 py-1 rounded text-sm hover:bg-blue-700 disabled:opacity-50"
              >
                Apply
              </button>
            </div>
          )}
        </div>
      </div>

      {/* Filters */}
      <div className="bg-white rounded-lg shadow-sm border border-neutral-200 p-4">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <div>
            <input
              type="text"
              placeholder="Search drivers..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full px-3 py-2 border border-neutral-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
          </div>
          <div>
            <select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              className="w-full px-3 py-2 border border-neutral-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              <option value="all">All Statuses</option>
              <option value="pending">Pending</option>
              <option value="kyc_required">KYC Required</option>
              <option value="approved">Approved</option>
              <option value="rejected">Rejected</option>
            </select>
          </div>
          <div className="flex items-center">
            <button
              onClick={() => refetchDrivers()}
              className="bg-blue-600 text-white px-4 py-2 rounded hover:bg-blue-700"
            >
              Refresh
            </button>
          </div>
        </div>
      </div>

      {/* Drivers Table */}
      <div className="bg-white rounded-lg shadow-sm border border-neutral-200 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-neutral-200">
            <thead className="bg-neutral-50">
              <tr>
                <th className="px-6 py-3 text-left">
                  <input
                    type="checkbox"
                    checked={selectedItems.size === filteredDrivers.length && filteredDrivers.length > 0}
                    onChange={handleSelectAll}
                    className="rounded border-neutral-300"
                  />
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-neutral-500 uppercase">Driver</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-neutral-500 uppercase">License</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-neutral-500 uppercase">KYC Status</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-neutral-500 uppercase">Performance</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-neutral-500 uppercase">Status</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-neutral-500 uppercase">Actions</th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-neutral-200">
              {filteredDrivers.map((driver: any) => (
                <tr key={driver.id} className="hover:bg-neutral-50">
                  <td className="px-6 py-4">
                    <input
                      type="checkbox"
                      checked={selectedItems.has(driver.id)}
                      onChange={() => handleSelectItem(driver.id)}
                      className="rounded border-neutral-300"
                    />
                  </td>
                  <td className="px-6 py-4">
                    <div>
                      <div className="font-medium text-neutral-900">{driver.full_name}</div>
                      <div className="text-sm text-neutral-500">{driver.email}</div>
                      <div className="text-sm text-neutral-500">{driver.phone}</div>
                    </div>
                  </td>
                  <td className="px-6 py-4">
                    <div className="text-sm">
                      <div className="font-medium">{driver.license_number}</div>
                      <div>Type: {driver.license_type}</div>
                      <div>Expires: {new Date(driver.license_expiry_date).toLocaleDateString()}</div>
                    </div>
                  </td>
                  <td className="px-6 py-4">
                    <div className="text-sm">
                      <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                        (driver.kyc_documents_count || 0) >= 3 ? 'bg-green-100 text-green-800' : 'bg-yellow-100 text-yellow-800'
                      }`}>
                        {(driver.kyc_documents_count || 0)}/3 Documents
                      </span>
                    </div>
                  </td>
                  <td className="px-6 py-4">
                    <div className="text-sm">
                      <div>Experience: {driver.years_driving_experience} years</div>
                      <div>Score: {driver.performance_score || 'N/A'}</div>
                    </div>
                  </td>
                  <td className="px-6 py-4">
                    <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                      driver.status === 'approved' ? 'bg-green-100 text-green-800' :
                      driver.status === 'pending' ? 'bg-yellow-100 text-yellow-800' :
                      driver.status === 'kyc_required' ? 'bg-orange-100 text-orange-800' :
                      'bg-red-100 text-red-800'
                    }`}>
                      {driver.status.replace('_', ' ')}
                    </span>
                  </td>
                  <td className="px-6 py-4 text-sm">
                    <button className="text-blue-600 hover:text-blue-900 mr-2">Review</button>
                    {driver.status === 'pending' && (
                      <>
                        <button className="text-green-600 hover:text-green-900 mr-2">Approve</button>
                        <button className="text-red-600 hover:text-red-900">Reject</button>
                      </>
                    )}
                    {driver.status === 'kyc_required' && (driver.kyc_documents_count || 0) >= 3 && (
                      <button
                        onClick={() => completeDriverKYC(driver.id)}
                        className="bg-purple-600 text-white px-3 py-1 rounded text-xs hover:bg-purple-700"
                      >
                        Complete KYC
                      </button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {filteredDrivers.length === 0 && (
          <div className="text-center py-12">
            <p className="text-neutral-500">No driver applications found matching your criteria.</p>
          </div>
        )}
      </div>
    </div>
  );

  const renderAutomation = () => (
    <div className="space-y-6">
      <h2 className="text-2xl font-bold text-neutral-800">Automation Management</h2>
      <p className="text-neutral-600">Configure intelligent processing rules and monitor automation performance</p>

      {/* Automation Stats */}
      {systemStats && (
        <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
          <div className="bg-white rounded-lg shadow-sm border border-neutral-200 p-6 text-center">
            <div className="text-2xl font-bold text-green-600">{systemStats.automationRate?.toFixed(1)}%</div>
            <div className="text-sm text-neutral-600">Automation Rate</div>
          </div>
          <div className="bg-white rounded-lg shadow-sm border border-neutral-200 p-6 text-center">
            <div className="text-2xl font-bold text-blue-600">{systemStats.autoApprovedRequests || 0}</div>
            <div className="text-sm text-neutral-600">Auto-Approved</div>
          </div>
          <div className="bg-white rounded-lg shadow-sm border border-neutral-200 p-6 text-center">
            <div className="text-2xl font-bold text-orange-600">{systemStats.manualReviewRequests || 0}</div>
            <div className="text-sm text-neutral-600">Manual Review</div>
          </div>
          <div className="bg-white rounded-lg shadow-sm border border-neutral-200 p-6 text-center">
            <div className="text-2xl font-bold text-purple-600">{systemStats.averageProcessingTime?.toFixed(0)}ms</div>
            <div className="text-sm text-neutral-600">Avg Processing Time</div>
          </div>
        </div>
      )}

      {/* Automation Rules Management - Placeholder for now */}
      <div className="bg-white rounded-lg shadow-sm border border-neutral-200 p-8 text-center">
        <div className="text-neutral-400 mb-4">
          <svg className="w-16 h-16 mx-auto" fill="currentColor" viewBox="0 0 24 24">
            <path d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z"/>
          </svg>
        </div>
        <h3 className="text-lg font-semibold text-neutral-800 mb-2">Automation Rules</h3>
        <p className="text-neutral-600">Advanced automation rule configuration coming soon...</p>
        <p className="text-sm text-neutral-500 mt-2">Manage risk-based processing, bulk approvals, and intelligent workflows</p>
      </div>
    </div>
  );

  const renderAnalytics = () => (
    <div className="space-y-6">
      <h2 className="text-2xl font-bold text-neutral-800">Business Intelligence</h2>
      <p className="text-neutral-600">Advanced analytics and predictive insights</p>

      {/* Analytics Dashboard - Placeholder for now */}
      <div className="bg-white rounded-lg shadow-sm border border-neutral-200 p-8 text-center">
        <div className="text-neutral-400 mb-4">
          <svg className="w-16 h-16 mx-auto" fill="currentColor" viewBox="0 0 24 24">
            <path d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z"/>
          </svg>
        </div>
        <h3 className="text-lg font-semibold text-neutral-800 mb-2">Business Analytics</h3>
        <p className="text-neutral-600">Comprehensive business intelligence tools coming soon...</p>
        <button
          onClick={() => window.open('/admin/advanced-analytics', '_blank')}
          className="mt-4 bg-blue-600 text-white px-6 py-2 rounded-lg hover:bg-blue-700"
        >
          View Current Analytics →
        </button>
      </div>
    </div>
  );

  const renderSecurity = () => (
    <div className="space-y-6">
      <h2 className="text-2xl font-bold text-neutral-800">Security Center</h2>

      {/* Alerts */}
      <div className="bg-white rounded-lg shadow-sm border border-neutral-200 overflow-hidden">
        <div className="p-6 border-b border-neutral-200 flex items-center justify-between">
          <h3 className="text-lg font-semibold text-neutral-800">Security Alerts</h3>
          <span className="text-sm text-neutral-600">
            {alerts.filter(a => !a.acknowledged).length} active
          </span>
        </div>
        <div className="divide-y divide-neutral-200">
          {alerts.map(alert => (
            <div key={alert.id} className={`p-6 ${!alert.acknowledged ? 'bg-yellow-50' : ''}`}>
              <div className="flex items-start justify-between">
                <div className="flex-1">
                  <div className="flex items-center space-x-2 mb-2">
                    <span className={`px-2 py-1 rounded-full text-xs font-medium ${getAlertColor(alert)}`}>
                      {alert.priority.toUpperCase()}
                    </span>
                    <h4 className="font-medium text-neutral-800">{alert.title}</h4>
                  </div>
                  <p className="text-neutral-600 mb-2">{alert.message}</p>
                  <div className="text-xs text-neutral-500">
                    {new Date(alert.timestamp).toLocaleString()}
                  </div>
                </div>
                {!alert.acknowledged && (
                  <button
                    onClick={() => acknowledgeAlert(alert.id)}
                    className="ml-4 px-3 py-1 bg-blue-600 text-white text-sm rounded hover:bg-blue-700"
                  >
                    Acknowledge
                  </button>
                )}
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );

  const getAlertColor = (alert: any) => {
    switch (alert.type) {
      case 'error': return 'bg-red-100 text-red-800';
      case 'warning': return 'bg-yellow-100 text-yellow-800';
      case 'success': return 'bg-green-100 text-green-800';
      default: return 'bg-blue-100 text-blue-800';
    }
  };

  if (statsLoading) {
    return (
      <div className="min-h-screen bg-neutral-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
          <p className="mt-4 text-neutral-600">Loading admin console...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-neutral-50">
      {/* Header */}
      <div className="bg-white shadow-sm border-b border-neutral-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-4">
              <StashitLogo className="h-8 w-auto" />
              <div>
                <h1 className="text-2xl font-bold text-neutral-900">Admin Console</h1>
                <p className="text-neutral-600">Unified system management and intelligence</p>
              </div>
            </div>
            <div className="flex items-center space-x-4">
              <div className="text-right">
                <div className="text-sm text-neutral-600">Last updated</div>
                <div className="text-sm font-medium text-neutral-800">
                  {new Date().toLocaleTimeString()}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
        {/* Navigation */}
        <div className="bg-white rounded-lg shadow-sm border border-neutral-200 mb-6">
          <div className="border-b border-neutral-200">
            <nav className="flex">
              {[
                { id: 'dashboard', label: 'Dashboard', icon: '📊' },
                { id: 'errands', label: 'Errands', icon: '📦' },
                { id: 'couriers', label: 'Couriers', icon: '🚚' },
                { id: 'drivers', label: 'Drivers', icon: '👨‍🚗' },
                { id: 'automation', label: 'Automation', icon: '🤖' },
                { id: 'analytics', label: 'Analytics', icon: '📈' },
                { id: 'security', label: 'Security', icon: '🛡️' }
              ].map((tab) => (
                <button
                  key={tab.id}
                  onClick={() => setActiveTab(tab.id as any)}
                  className={`flex items-center space-x-2 px-6 py-4 text-sm font-medium border-b-2 transition-colors ${
                    activeTab === tab.id
                      ? 'border-blue-500 text-blue-600'
                      : 'border-transparent text-neutral-500 hover:text-neutral-700 hover:border-neutral-300'
                  }`}
                >
                  <span>{tab.icon}</span>
                  <span>{tab.label}</span>
                </button>
              ))}
            </nav>
          </div>
        </div>

        {/* Content */}
        {activeTab === 'dashboard' && renderDashboard()}
        {activeTab === 'errands' && renderErrandsTab()}
        {activeTab === 'couriers' && renderCouriersTab()}
        {activeTab === 'drivers' && renderDriversTab()}
        {activeTab === 'automation' && renderAutomation()}
        {activeTab === 'analytics' && renderAnalytics()}
        {activeTab === 'security' && renderSecurity()}
      </div>
    </div>
  );
};

export default AdminConsole;
